'use client';
import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * 'page'    — full-screen error (default, existing behaviour)
   * 'section' — compact inline card for chart/table sections
   */
  variant?: 'page' | 'section';
  /** Label shown in the section variant header (e.g. "Enrollment Chart") */
  sectionLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info);

    // Surface to audit trail asynchronously — import lazily to avoid circular deps
    import('@/lib/auditLogger').then(({ logAuditEvent }) => {
      logAuditEvent({
        eventCategory: 'data',
        eventAction: 'render_error',
        resourceType: this.props.sectionLabel ?? 'ui_section',
        description: `React render error in ${this.props.sectionLabel ?? 'UI section'}: ${error.message}`,
        severity: 'warning',
        metadata: {
          componentStack: info.componentStack?.slice(0, 500) ?? '',
          errorMessage: error.message,
        },
      }).catch(() => {/* never throw from audit */});
    }).catch(() => {/* ignore import errors */});
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  renderSection() {
    const { sectionLabel } = this.props;
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 flex flex-col items-center text-center gap-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
        </div>
        <div>
          <p className="text-sm font-600 text-red-700">
            {sectionLabel ? `${sectionLabel} failed to render` : 'Section failed to render'}
          </p>
          <p className="text-xs text-red-500 mt-1">
            {this.state.error?.message ?? 'An unexpected error occurred in this section.'}
          </p>
        </div>
        <button
          onClick={this.handleRetry}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-600 hover:bg-red-700 transition-colors"
        >
          <ArrowPathIcon className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  renderPage() {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-surface rounded-2xl shadow-lg border border-border p-8 text-center">
          {/* Icon */}
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mx-auto mb-5">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>

          {/* Branding */}
          <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">
            SILAG SIMS
          </p>

          {/* Heading */}
          <h1 className="text-xl font-bold text-text-primary mb-2">
            Something went wrong
          </h1>

          {/* Description */}
          <p className="text-sm text-text-secondary mb-6 leading-relaxed">
            An unexpected error occurred in this section of the portal. You can try again or return to your dashboard.
          </p>

          {/* Error detail (collapsed) */}
          {this.state.error && (
            <details className="text-left mb-6 bg-gray-50 rounded-lg p-3 border border-gray-200">
              <summary className="text-xs font-medium text-text-secondary cursor-pointer select-none">
                Error details
              </summary>
              <p className="mt-2 text-xs text-red-600 font-mono break-all whitespace-pre-wrap">
                {this.state.error.message}
              </p>
            </details>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={this.handleRetry}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Try Again
            </button>
            <a
              href="/"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-border text-text-primary text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
              </svg>
              Go to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.variant === 'section' ? this.renderSection() : this.renderPage();
    }
    return this.props.children;
  }
}
